/**
 * What a card opens: the item, before it plays.
 *
 * Selecting a card used to start the stream immediately. That is wrong for the same
 * reason it is wrong on every other TV app — a card is a poster and a title, and the
 * viewer picking it has not yet decided anything. They want to know what it is, how long
 * it runs, and whether they already started it. Playing on the first press turns a
 * browsing gesture into a commitment, and the only way back out is a stream that has
 * already been minted on the server.
 *
 * Live channels are the exception and go straight to the picture: a channel has no
 * synopsis to read and no position to resume, and the whole point of a channel is that it
 * is already running.
 */
import { useNavigate, useParams } from 'react-router-dom';
import type { CatalogItemDto, WatchProgressDto } from '@filmodasi/shared';
import { useApi } from '../../../api/hooks';
import { ErrorBox, Spinner } from '../../../components/ui';
import { useFocusable } from '../focus/useFocusable';
import { TvRecommend } from '../TvRecommend';

function minutes(sec: number): string {
  const m = Math.round(sec / 60);
  if (m < 60) return `${m} dk`;
  return `${Math.floor(m / 60)} sa ${m % 60} dk`;
}

function Action({
  id,
  label,
  primary,
  onSelect,
}: {
  id: string;
  label: string;
  primary?: boolean;
  onSelect: () => void;
}) {
  const { ref, focused } = useFocusable<HTMLButtonElement>({
    id,
    group: 'detail:actions',
    onSelect,
    // The one thing a viewer almost always wants is the first thing under the cursor.
    autoFocus: primary,
  });
  return (
    <button
      ref={ref}
      type="button"
      className={`tvaction${primary ? ' tvaction--primary' : ''}${focused ? ' is-focused' : ''}`}
      onClick={onSelect}
    >
      {label}
    </button>
  );
}

export function TvItemDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const item = useApi<CatalogItemDto>(id ? `/api/catalog/item/${id}` : null);
  // 200 with a null body when nothing has been watched, so a miss is not an error.
  const progress = useApi<WatchProgressDto | null>(id ? `/api/progress/${id}` : null);

  if (item.loading && !item.data) return <Spinner />;
  if (item.error) return <ErrorBox message={item.error} onRetry={item.reload} />;
  if (!item.data) return null;

  const data = item.data;
  const resume = progress.data;
  const canResume = Boolean(resume && resume.positionSec > 0 && !resume.completed);

  const play = (fromStart: boolean) =>
    navigate(fromStart ? `/watch/${data.id}?restart=1` : `/watch/${data.id}`);

  const meta = [
    data.year ? String(data.year) : null,
    data.durationSec ? minutes(data.durationSec) : null,
    data.rating ? `★ ${data.rating}` : null,
    // From enrichment, so absent on most of the catalog until the crawl reaches it.
    data.ageRating,
    data.genres.length > 0 ? data.genres.join(' · ') : null,
  ]
    .filter(Boolean)
    .join('  ·  ');

  /*
   * Cast is trimmed for a television.
   *
   * A provider cast list runs to twenty names. At three metres that is a paragraph of
   * small text nobody reads, and it pushes the play button off the screen — the one
   * thing this page exists for. The first four are the billed leads, which is the part
   * that actually tells you whether you want to watch it.
   */
  const cast = data.cast.slice(0, 4).join(', ');

  return (
    <div className="tvitem">
      {/* Backdrop rather than a framed poster: at three metres the picture is the thing
          that identifies the film, and the text needs somewhere dark to sit. */}
      {(data.poster ?? data.logo) && (
        <img className="tvitem__art" src={data.poster ?? data.logo!} alt="" />
      )}

      <div className="tvitem__body">
        <h1 className="tvitem__title">{data.title}</h1>
        {meta && <p className="tvitem__meta">{meta}</p>}

        {canResume && resume && (
          <div className="tvitem__resume">
            <div className="tvitem__bar">
              <span style={{ width: `${Math.min(100, resume.percent)}%` }} />
            </div>
            <span>
              {minutes(resume.positionSec)} izlendi
              {/* The provider's duration is often missing, and a film with no known length
                  still has a position worth showing. */}
              {resume.durationSec != null &&
                resume.durationSec > resume.positionSec &&
                ` · ${minutes(resume.durationSec - resume.positionSec)} kaldı`}
            </span>
          </div>
        )}

        {data.plot && <p className="tvitem__plot">{data.plot}</p>}

        {(cast || data.director) && (
          <p className="tvitem__credits">
            {cast && (
              <>
                <span className="tvitem__label">Oyuncular</span> {cast}
              </>
            )}
            {cast && data.director && '   ·   '}
            {data.director && (
              <>
                <span className="tvitem__label">Yönetmen</span> {data.director}
              </>
            )}
          </p>
        )}

        <div className="tvitem__actions">
          {canResume ? (
            <>
              <Action id="detail:resume" label="▶  Devam et" primary onSelect={() => play(false)} />
              <Action id="detail:restart" label="Baştan oynat" onSelect={() => play(true)} />
            </>
          ) : (
            <Action id="detail:play" label="▶  Oynat" primary onSelect={() => play(false)} />
          )}
          <TvRecommend itemId={data.id} />
        </div>
      </div>
    </div>
  );
}
